import { ActionButtonProps } from '@euler/app/components/BottomAction';
import { MemberPickerBottomSheet } from '@euler/app/components/MemberPickerBottomSheet';
import { PageContainerForIpad } from '@euler/app/components/PageContainerForIpad';
import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { Colors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { Icon } from '@euler/components/Icomoon';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  Platform,
  SectionList,
  SectionListData,
  StyleProp,
  StyleSheet,
  TextInput,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { runOnUI } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Fill } from 'react-slot-fill';
import * as InspectionSiteGrid from '../functions/InspectionSiteGrid';
import {
  TaskInspectionProps,
  useTaskInspection,
} from '../functions/useTaskInspection';
import { SiteInspectionSidePanelFlow } from '../site-inspection/SiteInspectionSidePanelFlow';
import { HiddenSiteBottomSheet } from './HiddenSiteBottomSheet';
import { InspectionSiteGridView } from './InspectionSiteGrid';
import { InspectionSiteGridHeader } from './InspectionSiteGridHeader';

export const InspectionScreenContentForIpad = memo(
  ({ flowId, onCancel }: TaskInspectionProps) => {
    const safeAreaInsets = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const { width } = useWindowDimensions();
    const { detail } = useTaskContext();

    const {
      flow,
      user,
      sections,
      listRef,
      isMemberListVisible,
      keyword,
      setIsMemberListVisible,
      siteInspectionFlow,
      activeSectionForHiddenSites,
      setActiveSectionForHiddenSites,
      onKeywordChange,
      onSiteSelected,
      onSiteDismissed,
      onTechnicianPress,
      onMemberSelected,
      onMorePress,
      onHiddenSiteSelected,
      onScroll,
      onPreview,
    } = useTaskInspection({ flowId, onCancel, mode: 'sidePanel' });

    const [isSiteInspectionPresented, setIsSiteInspectionPresented] =
      useState(false);

    const onSiteInspectionPresented = useCallback(() => {
      setIsSiteInspectionPresented(true);
    }, []);

    const onSiteInspectionDismissed = useCallback(() => {
      setIsSiteInspectionPresented(false);
      onSiteDismissed();
    }, [onSiteDismissed]);

    const isLandscape = useIsLandscape();
    const siteGridFlex = isLandscape ? 0.55 : 0.4;
    const siteGridWidth = Math.round(width * siteGridFlex);
    const moreButtonRef = useRef(null);

    const actions = useMemo(() => {
      const res: ActionButtonProps[] = [
        {
          bordered: true,
          text: '预览报告',
          variant: 'white',
          onPress: onPreview,
        },
        {
          bordered: true,
          text: '更多',
          variant: 'white',
          onPress: () => onMorePress(moreButtonRef),
          style: { marginLeft: rpx(15) },
          buttonRef: moreButtonRef,
          iconRight: (
            <Icon name="more-vertical" size={rpx(24)} color={Colors.White} />
          ),
        },
      ];
      return res;
    }, [onMorePress, onPreview, rpx]);

    return (
      <PageContainerForIpad
        name="inspection"
        title="常规检测"
        subTitle={flow?.name ?? '车辆检测'}
        actions={actions}
        titleStyle={isLandscape ? undefined : { marginLeft: rpx(10) }}
        headerStyle={{ paddingTop: rpx(15), paddingBottom: rpx(15) }}
        footerStyle={{ paddingTop: rpx(15), paddingBottom: rpx(15) }}
      >
        <HStack
          alignItems="stretch"
          css={`
            flex: 1;
            padding: 0 ${rpx(15)}px;
          `}
        >
          <LayoutProviderView
            css={`
              width: ${siteGridWidth}px;
              border-radius: ${rpx(20)}px;
              overflow: hidden;
              background-color: #fff;
              padding: ${rpx(10)}px 0;
            `}
          >
            <SectionList
              ref={listRef as any}
              onScroll={e => {
                runOnUI(onScroll)(e.nativeEvent.contentOffset.y);
              }}
              showsVerticalScrollIndicator={Platform.OS === 'web'}
              stickySectionHeadersEnabled={true}
              sections={sections}
              initialNumToRender={10}
              keyExtractor={x => x.groupId}
              viewabilityConfig={{
                itemVisiblePercentThreshold: 95,
                minimumViewTime: 50,
              }}
              renderSectionHeader={({
                section,
              }: {
                section: SectionListData<
                  InspectionSiteGrid.GroupSectionDataItem,
                  InspectionSiteGrid.GroupSection
                >;
              }) => {
                return (
                  <InspectionSiteGridHeader
                    width={siteGridWidth}
                    groupId={section.groupId}
                    name={section.title}
                    hasMore={
                      section.hiddenSites != null &&
                      section.hiddenSites.length > 0
                    }
                    onMorePress={() => setActiveSectionForHiddenSites(section)}
                  />
                );
              }}
              renderItem={({ item: section }) => (
                <InspectionSiteGridView
                  items={section.items}
                  width={siteGridWidth}
                  numberOfColumns={isLandscape ? 4 : 3}
                  onCellPress={onSiteSelected}
                />
              )}
            />
          </LayoutProviderView>
          <View
            css={`
              flex: 1;
              margin-left: ${rpx(15)}px;
              background-color: #fff;
              border-radius: ${rpx(20)}px;
              overflow: hidden;
            `}
          >
            {!isSiteInspectionPresented && (
              <View style={StyleSheet.absoluteFill}>
                <EmptyView
                  message="选择部位开始检测"
                  iconSize={rpx(100)}
                  messageStyle={{ fontSize: rpx(24) }}
                  css={`
                    align-self: center;
                  `}
                />
              </View>
            )}

            <SiteInspectionSidePanelFlow
              ref={siteInspectionFlow}
              onPresent={onSiteInspectionPresented}
              onDismiss={onSiteInspectionDismissed}
            />
          </View>
        </HStack>

        <Fill name="inspection/page-container/header/left">
          {isLandscape ? (
            <SearchInput
              value={keyword}
              onChangeText={onKeywordChange}
              css={`
                width: ${rpx(isLandscape ? 320 : 280)}px;
                margin-left: ${rpx(20)}px;
              `}
            />
          ) : null}
        </Fill>

        <Fill name="inspection/page-container/header/right">
          {isLandscape ? (
            <View
              css={`
                align-items: flex-end;
              `}
            >
              <Typography.Label
                bold
                size={rpx(28)}
                color={'white'}
                css={`
                  margin-left: ${rpx(10)}px;
                `}
              >
                {detail.basicInfo.licensePlateNo}
              </Typography.Label>
              <Typography.Label
                regular
                size={rpx(16)}
                color={Colors.White4}
                numberOfLines={1}
                css={`
                  margin-left: ${rpx(10)}px;
                `}
              >
                {detail.basicInfo.vehicleName}
              </Typography.Label>
            </View>
          ) : (
            <SearchInput
              value={keyword}
              onChangeText={onKeywordChange}
              searchInputPlaceholder={
                width <= rpx(1024) ? '搜索项目' : undefined
              }
              css={`
                width: ${width > rpx(1024) ? rpx(300) : rpx(180)}px;
                margin-left: ${rpx(20)}px;
              `}
            />
          )}
        </Fill>

        <Fill name="inspection/page-container/footer/left">
          {isLandscape ? null : (
            <View
              css={`
                align-items: flex-start;
                margin-bottom: ${rpx(16)}px;
              `}
            >
              <Typography.Label bold size={rpx(28)} color={'white'}>
                {detail.basicInfo.licensePlateNo}
              </Typography.Label>
              <Typography.Label
                regular
                size={rpx(16)}
                color={Colors.White4}
                numberOfLines={1}
              >
                {detail.basicInfo.vehicleName}
              </Typography.Label>
            </View>
          )}
          <TouchableOpacity
            onPress={onTechnicianPress}
            css={`
              flex-direction: row;
              align-items: center;
            `}
          >
            <Typography.Label
              light
              color={Colors.White7_5}
              size={rpx(18)}
              lineHeight={rpx(24)}
            >
              检测技师: {flow?.assignedTo.name ?? user.name}
            </Typography.Label>
            <Icon
              name="pencil"
              color={Colors.White7_5}
              size={rpx(12)}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            />
          </TouchableOpacity>
        </Fill>

        <MemberPickerBottomSheet
          show={isMemberListVisible}
          title={`${flow?.name} - 重新派工`}
          onSelect={onMemberSelected}
          onDismiss={() => setIsMemberListVisible(false)}
        />

        <HiddenSiteBottomSheet
          show={activeSectionForHiddenSites != null}
          items={activeSectionForHiddenSites?.hiddenSites ?? []}
          topMargin={safeAreaInsets.top}
          onSelect={onHiddenSiteSelected}
          onDismiss={() => {
            setActiveSectionForHiddenSites(undefined);
          }}
        />
      </PageContainerForIpad>
    );
  },
);

const SearchInput = memo(
  ({
    value,
    style,
    searchInputPlaceholder = '搜索检测项目或指标',
    searchInputStyle,
    onChangeText,
  }: {
    value?: string;
    searchInputPlaceholder?: string;
    style?: StyleProp<ViewStyle>;
    searchInputStyle?: StyleProp<TextStyle>;
    onChangeText?: (value: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          flex-wrap: nowrap;
          height: ${rpx(56)}px;
          border-radius: ${rpx(28)}px;
          border-width: 1px;
          border-color: ${Colors.White8};
          padding: 0 ${rpx(20)}px;
        `}
        style={style}
      >
        <Icon name="search" size={rpx(28)} color="#fff" />
        <TextInput
          placeholder={searchInputPlaceholder}
          value={value ?? ''}
          onChangeText={onChangeText}
          placeholderTextColor={Colors.White5_5}
          css={`
            flex: 1;
            margin-left: ${rpx(10)}px;
            font-size: ${rpx(20)}px;
            color: #fff;
          `}
          style={searchInputStyle}
        />
      </View>
    );
  },
);
